import {
  IHealthScribeWebSocketProvider,
  IAudioChunk,
  ITranscriptionMessage,
} from "../../../core/ports/HealthScribe";

export class HealthScribeWebSocketProvider
  implements IHealthScribeWebSocketProvider
{
  private ws: WebSocket | null = null;
  private sessionId: string | null = null;
  private websocketUrl: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private crc32Table: Uint32Array | null = null;
  private sequenceNumber = 0;
  private transcriptionBuffer: any[] = [];
  private clinicId: number | null = null;
  private lastTranscriptionMessage: string | null = null;

  // Callback properties
  public onTranscription: ((message: ITranscriptionMessage) => void) | null =
    null;
  public onError: ((error: string) => void) | null = null;
  public onStatusChange:
    | ((status: "connected" | "disconnected" | "error") => void)
    | null = null;

  private handleAWSError(text: string): void {
    console.error(
      "[HealthScribe WebSocket Provider] AWS Error in binary message:",
      text
    );

    if (text.includes("X-Amz-Expires cannot exceed 300 seconds")) {
      console.error(
        "[HealthScribe WebSocket Provider] BACKEND FIX REQUIRED: WebSocket URL expiration must be ≤ 300 seconds (5 minutes)"
      );
      console.error(
        "[HealthScribe WebSocket Provider] Current backend is setting expiration > 300 seconds, reduce it to 300 or less"
      );
    }

    if (text.includes("Could not decode the audio stream")) {
      console.error(
        "[HealthScribe WebSocket Provider] AUDIO FORMAT ERROR: AWS cannot decode the audio stream"
      );
      console.error(
        "[HealthScribe WebSocket Provider] This typically means the audio format is incompatible"
      );
      console.error(
        "[HealthScribe WebSocket Provider] AWS Transcribe Medical requires:"
      );
      console.error("  - Raw PCM audio data (not WebM/Opus)");
      console.error("  - 16 kHz sample rate");
      console.error("  - 16-bit depth");
      console.error("  - Mono (1 channel)");
      console.error("  - Little-endian encoding");
      console.error(
        "[HealthScribe WebSocket Provider] Browser MediaRecorder typically produces WebM/Opus which AWS cannot decode"
      );
      console.error(
        "[HealthScribe WebSocket Provider] SOLUTION: Need to convert WebM/Opus to PCM format before sending to AWS"
      );
    }

    if (this.onError) {
      if (text.includes("Could not decode the audio stream")) {
        this.onError(
          "Audio format incompatible: AWS Transcribe Medical requires PCM audio, but browser is recording in WebM/Opus format. Audio conversion needed."
        );
      } else if (text.includes("X-Amz-Expires")) {
        this.onError(
          `AWS Error: WebSocket URL expiration too long - backend must set expiration ≤ 300 seconds`
        );
      } else {
        this.onError(`AWS Error: ${text}`);
      }
    }
  }

  async connect(
    sessionId: string,
    clinicId: number,
    websocketUrl?: string
  ): Promise<void> {
    try {
      // Close any existing WebSocket connection before creating a new one
      if (this.ws && this.ws.readyState !== WebSocket.CLOSED) {
        // Remove event listeners to prevent unwanted callbacks
        this.ws.onopen = null;
        this.ws.onclose = null;
        this.ws.onerror = null;
        this.ws.onmessage = null;

        // Close the connection
        if (
          this.ws.readyState === WebSocket.CONNECTING ||
          this.ws.readyState === WebSocket.OPEN
        ) {
          this.ws.close(1000, "Starting new connection");
        }

        this.ws = null;
      }

      this.sessionId = sessionId;
      this.clinicId = clinicId;
      this.websocketUrl = websocketUrl || null;
      this.lastTranscriptionMessage = null; // Reset for new session

      if (!this.websocketUrl) {
        throw new Error("WebSocket URL is required for connection");
      }

      // Create real WebSocket connection
      // Note: AWS HealthScribe WebSocket URLs typically include authentication parameters

      // Check if we need specific protocols for AWS HealthScribe
      this.ws = new WebSocket(this.websocketUrl);

      // Set binary type for handling binary messages
      this.ws.binaryType = "blob"; // or 'arraybuffer' depending on preference

      // Set up WebSocket event handlers
      return new Promise((resolve, reject) => {
        if (!this.ws) {
          reject(new Error("Failed to create WebSocket"));
          return;
        }

        // onopen handler is set above with timeout clearing

        this.ws.onclose = (event) => {
          if (this.onStatusChange) {
            this.onStatusChange("disconnected");
          }

          // Attempt reconnection if not a clean close
          if (
            event.code !== 1000 &&
            this.reconnectAttempts < this.maxReconnectAttempts
          ) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error(
            "[HealthScribe WebSocket Provider] WebSocket error:",
            error
          );
          console.error(
            "[HealthScribe WebSocket Provider] This is likely an authentication issue."
          );
          console.error(
            "[HealthScribe WebSocket Provider] The backend must provide a properly signed AWS WebSocket URL."
          );
          console.error(
            "[HealthScribe WebSocket Provider] AWS Transcribe Medical requires SigV4 signing of the WebSocket URL."
          );

          if (this.onError) {
            this.onError(
              `WebSocket authentication error - backend must provide properly signed AWS WebSocket URL: ${error}`
            );
          }
          if (this.onStatusChange) {
            this.onStatusChange("error");
          }
          reject(error);
        };

        this.ws.onmessage = async (event) => {
          try {
            let data;

            // Handle binary messages (Blob/ArrayBuffer) - AWS event stream format
            if (event.data instanceof Blob) {
              // Convert Blob to ArrayBuffer for proper binary parsing
              const arrayBuffer = await event.data.arrayBuffer();
              const decoded = this.decodeAWSEventStreamMessage(arrayBuffer);

              if (decoded) {
                data = decoded;
              } else {
                return;
              }
            } else if (event.data instanceof ArrayBuffer) {
              const decoded = this.decodeAWSEventStreamMessage(event.data);

              if (decoded) {
                data = decoded;
              } else {
                return;
              }
            } else if (typeof event.data === "string") {
              // Handle text messages (fallback for non-event-stream messages)
              try {
                data = JSON.parse(event.data);
              } catch (error) {
                console.error(
                  "[HealthScribe WebSocket Provider] Failed to parse text message as JSON:",
                  error
                );
                return;
              }
            } else {
              console.error(
                "[HealthScribe WebSocket Provider] Received unknown message type:",
                typeof event.data
              );
              return;
            }

            // Handle various message types from AWS HealthScribe
            if (this.onTranscription) {
              let transcriptionMessage: ITranscriptionMessage | null = null;

              // Handle different possible message formats
              if (
                data.type === "transcription" ||
                data.messageType === "transcription"
              ) {
                transcriptionMessage = {
                  id: data.id || data.messageId || `msg_${Date.now()}`,
                  sessionId: this.sessionId!,
                  speaker: "talking",
                  // Previously we had the following but it wasn't working because HealthScribe always returned the label "doctor" even when patient was talking:
                  ///// speaker: data.speaker || data.speakerRole || "doctor",
                  message:
                    data.message || data.text || data.transcription || "",
                  timestamp:
                    data.timestamp || data.time || new Date().toISOString(),
                  confidence: data.confidence || data.score || 0.95,
                };
              }
              // Handle AWS Transcribe Medical format (new structure)
              else if (
                data.Transcript &&
                data.Transcript.Results &&
                data.Transcript.Results.length > 0
              ) {
                const result = data.Transcript.Results[0];
                if (result.Alternatives && result.Alternatives.length > 0) {
                  const alternative = result.Alternatives[0];
                  // Use consistent ID based on ResultId for both partial and final messages
                  // This allows the UI to update the same message instead of creating duplicates
                  const messageId =
                    result.ResultId ||
                    `msg_${Date.now()}_${Math.random()
                      .toString(36)
                      .substr(2, 9)}`;
                  transcriptionMessage = {
                    id: messageId,
                    sessionId: this.sessionId!,
                    speaker: "talking",
                    // Previously we had the following but it wasn't working because HealthScribe always returned the label "doctor" even when patient was talking:
                    ///// speaker: result.ChannelId === "ch_0" ? "doctor" : "patient", // Map channel to speaker
                    message: alternative.Transcript || "",
                    timestamp: new Date().toISOString(),
                    confidence: 0.95, // AWS Transcribe Medical doesn't provide overall confidence
                    isPartial: result.IsPartial || false, // Include partial status for UI handling
                  };
                }
              }
              // Handle AWS HealthScribe specific format (legacy)
              else if (data.Transcript || data.transcript) {
                const transcript = data.Transcript || data.transcript;
                transcriptionMessage = {
                  id: data.EventId || `msg_${Date.now()}`,
                  sessionId: this.sessionId!,
                  speaker: "talking",
                  // Previously we had the following but it wasn't working because HealthScribe always returned the label "doctor" even when patient was talking:
                  ///// speaker: data.ParticipantRole || "doctor",
                  message: transcript.Content || transcript.content || "",
                  timestamp: data.Timestamp || new Date().toISOString(),
                  confidence: transcript.Confidence || 0.95,
                };
              }
              // Handle AWS Transcribe streaming format (legacy)
              else if (data.results && data.results.length > 0) {
                const result = data.results[0];
                if (result.alternatives && result.alternatives.length > 0) {
                  const alternative = result.alternatives[0];
                  transcriptionMessage = {
                    id: result.resultId || `msg_${Date.now()}`,
                    sessionId: this.sessionId!,
                    speaker: "talking",
                    // Previously we had the following but it wasn't working because HealthScribe always returned the label "doctor" even when patient was talking:
                    ///// speaker: result.speakerLabel || "doctor",
                    message: alternative.transcript || "",
                    timestamp: new Date().toISOString(),
                    confidence: alternative.confidence || 0.95,
                  };
                }
              }

              if (transcriptionMessage && transcriptionMessage.message.trim()) {
                // The AWS HealthScribe server has a bug where during periods of no speech, the ambient background noise can get transcribed to a series of segments saying "I".
                // Filter out repeated "I" segments caused by background noise
                const currentMessage = transcriptionMessage.message.trim();
                const isRepeatedI =
                  currentMessage === "I" &&
                  this.lastTranscriptionMessage === "I";

                if (!isRepeatedI) {
                  this.onTranscription(transcriptionMessage);
                  this.lastTranscriptionMessage = currentMessage;
                }
              }
            }

            // Handle status messages
            if (data.type === "status" || data.messageType === "status") {
              // Status updates are handled silently
            }

            // Handle error messages
            if (data.type === "error" || data.messageType === "error") {
              console.error(
                "[HealthScribe WebSocket Provider] Server error:",
                data.error || data.message
              );
              if (this.onError) {
                this.onError(
                  data.error || data.message || "Unknown server error"
                );
              }
            }
          } catch (error) {
            console.error(
              "[HealthScribe WebSocket Provider] Error handling message:",
              error
            );
            if (this.onError) {
              this.onError(`Error handling WebSocket message: ${error}`);
            }
          }
        };

        // Set connection timeout
        const timeoutId = setTimeout(() => {
          if (this.ws) {
            // Clean up the WebSocket if it's still connecting
            if (this.ws.readyState === WebSocket.CONNECTING) {
              this.ws.close();
              this.ws = null;
              reject(
                new Error(
                  `WebSocket connection timeout after 10 seconds. URL: ${this.websocketUrl?.substring(
                    0,
                    100
                  )}...`
                )
              );
            }
          }
        }, 10000); // 10 second timeout

        // Clear timeout if connection succeeds
        this.ws.onopen = () => {
          clearTimeout(timeoutId);

          this.reconnectAttempts = 0;
          if (this.onStatusChange) {
            this.onStatusChange("connected");
          }

          // AWS Transcribe Medical WebSocket expects proper SigV4 authentication
          // The WebSocket URL should already be pre-signed by the backend with SigV4

          // Backend TODO: Ensure the WebSocket URL includes proper AWS SigV4 signing
          // The URL should contain authentication parameters like X-Amz-Credential, X-Amz-Signature, etc.

          resolve();
        };
      });
    } catch (error) {
      console.error(
        "[HealthScribe WebSocket Provider] Failed to connect:",
        error
      );
      if (this.onError) {
        this.onError(`Failed to connect WebSocket: ${error}`);
      }
      if (this.onStatusChange) {
        this.onStatusChange("error");
      }
      throw error;
    }
  }

  async disconnect(): Promise<void> {
    try {
      if (this.ws) {
        // Clean close
        this.ws.close(1000, "Normal closure");
        this.ws = null;
      }

      this.sessionId = null;
      this.websocketUrl = null;
      this.lastTranscriptionMessage = null; // Reset filter

      if (this.onStatusChange) {
        this.onStatusChange("disconnected");
      }
    } catch (error) {
      console.error(
        "[HealthScribe WebSocket Provider] Failed to disconnect:",
        error
      );
      throw error;
    }
  }

  async sendAudioChunk(chunk: IAudioChunk): Promise<void> {
    try {
      if (!this.ws || !this.isConnected()) {
        throw new Error("WebSocket is not connected");
      }

      // AWS Transcribe Medical expects audio data in event stream encoding format
      if (chunk.data.type.includes("wav") || chunk.data.type.includes("pcm")) {
        if (chunk.data.size > 0) {
          try {
            // Convert Blob to ArrayBuffer for event stream encoding
            const audioArrayBuffer = await chunk.data.arrayBuffer();

            // Create AWS event stream encoded message
            const eventStreamMessage =
              this.createAWSAudioEventMessage(audioArrayBuffer);

            // Send the event stream encoded message
            this.ws.send(eventStreamMessage);
          } catch (error) {
            console.error(
              "[HealthScribe WebSocket Provider] Failed to send PCM chunk:",
              error
            );
            if (this.onError) {
              this.onError(`Failed to send PCM audio chunk: ${error}`);
            }
          }
        }
      } else if (
        chunk.data.type.includes("webm") ||
        chunk.data.type.includes("opus")
      ) {
        console.error(
          "[HealthScribe WebSocket Provider] AUDIO FORMAT ERROR: AWS expects PCM/WAV, but received:",
          chunk.data.type
        );

        if (this.onError) {
          this.onError(
            "Audio format incompatible: AWS Transcribe Medical requires PCM audio, but browser is recording in WebM/Opus format. Audio conversion needed."
          );
        }
      } else {
        console.error(
          "[HealthScribe WebSocket Provider] Unknown audio format:",
          chunk.data.type
        );

        if (this.onError) {
          this.onError(
            `Unknown audio format: ${chunk.data.type}. Expected PCM/WAV format for AWS Transcribe Medical.`
          );
        }
      }
    } catch (error) {
      console.error(
        "[HealthScribe WebSocket Provider] Error sending audio chunk:",
        error
      );
      if (this.onError) {
        this.onError(`Error sending audio chunk: ${error}`);
      }
    }
  }

  /**
   * Decodes AWS event stream binary messages into JSON objects
   */
  private decodeAWSEventStreamMessage(buffer: ArrayBuffer): any | null {
    try {
      const view = new DataView(buffer);
      let offset = 0;

      // Read prelude (8 bytes)
      if (buffer.byteLength < 12) {
        return null;
      }

      const totalLength = view.getUint32(offset, false); // Big-endian
      offset += 4;
      const headersLength = view.getUint32(offset, false); // Big-endian
      offset += 4;

      // Skip prelude CRC (4 bytes)
      offset += 4;

      // Skip headers (we'll extract the JSON payload directly)
      offset += headersLength;

      // Calculate payload length
      const payloadLength = totalLength - 12 - headersLength - 4; // total - prelude - headers - message_crc

      if (payloadLength <= 0) {
        return {};
      }

      // Extract payload (JSON data)
      const payloadBytes = new Uint8Array(buffer, offset, payloadLength);
      const payloadText = new TextDecoder().decode(payloadBytes);

      // Parse JSON payload
      return JSON.parse(payloadText);
    } catch (error) {
      console.error(
        "[HealthScribe WebSocket Provider] Failed to decode event stream message:",
        error
      );

      // Fallback: Try to extract JSON from the raw text content
      try {
        const text = new TextDecoder().decode(buffer);
        const jsonMatch = text.match(/\{.*\}/);
        if (jsonMatch) {
          return JSON.parse(jsonMatch[0]);
        }
      } catch (fallbackError) {
        console.error(
          "[HealthScribe WebSocket Provider] Fallback parsing also failed:",
          fallbackError
        );
      }

      return null;
    }
  }

  /**
   * Creates an AWS event stream encoded message for audio data
   * This follows the AWS event stream encoding specification for Transcribe Medical
   */
  private createAWSAudioEventMessage(audioData: ArrayBuffer): ArrayBuffer {
    // Create headers for the AudioEvent
    const headers = this.createEventStreamHeaders([
      { name: ":content-type", value: "application/octet-stream", type: 7 },
      { name: ":event-type", value: "AudioEvent", type: 7 },
      { name: ":message-type", value: "event", type: 7 },
    ]);

    // Calculate total message length
    const preludeLength = 8; // 4 bytes total length + 4 bytes headers length
    const headersCrcLength = 4;
    const payloadCrcLength = 4;
    const totalLength =
      preludeLength +
      headersCrcLength +
      headers.byteLength +
      audioData.byteLength +
      payloadCrcLength;

    // Create the complete message buffer
    const messageBuffer = new ArrayBuffer(totalLength);
    const view = new DataView(messageBuffer);
    let offset = 0;

    // Write prelude (8 bytes)
    view.setUint32(offset, totalLength, false); // Big-endian total length
    offset += 4;
    view.setUint32(offset, headers.byteLength, false); // Big-endian headers length
    offset += 4;

    // Calculate and write prelude CRC
    const preludeArray = new Uint8Array(messageBuffer, 0, 8);
    const preludeCrc = this.calculateCRC32(preludeArray);
    view.setUint32(offset, preludeCrc, false);
    offset += 4;

    // Write headers
    const headersArray = new Uint8Array(headers);
    new Uint8Array(messageBuffer, offset, headers.byteLength).set(headersArray);
    offset += headers.byteLength;

    // Write audio payload
    const audioArray = new Uint8Array(audioData);
    new Uint8Array(messageBuffer, offset, audioData.byteLength).set(audioArray);
    offset += audioData.byteLength;

    // Calculate and write message CRC (prelude + headers + payload)
    const messageArray = new Uint8Array(messageBuffer, 0, offset);
    const messageCrc = this.calculateCRC32(messageArray);
    view.setUint32(offset, messageCrc, false);

    return messageBuffer;
  }

  /**
   * Creates event stream headers according to AWS specification
   */
  private createEventStreamHeaders(
    headerSpecs: Array<{ name: string; value: string; type: number }>
  ): ArrayBuffer {
    let totalSize = 0;
    const encodedHeaders: ArrayBuffer[] = [];

    // Encode each header
    for (const spec of headerSpecs) {
      const nameBytes = new TextEncoder().encode(spec.name);
      const valueBytes = new TextEncoder().encode(spec.value);

      const headerSize = 1 + nameBytes.length + 1 + 2 + valueBytes.length; // name_len + name + type + value_len + value
      const headerBuffer = new ArrayBuffer(headerSize);
      const view = new DataView(headerBuffer);
      let offset = 0;

      // Write header name length (1 byte)
      view.setUint8(offset, nameBytes.length);
      offset += 1;

      // Write header name
      new Uint8Array(headerBuffer, offset, nameBytes.length).set(nameBytes);
      offset += nameBytes.length;

      // Write header value type (1 byte)
      view.setUint8(offset, spec.type);
      offset += 1;

      // Write header value length (2 bytes, big-endian)
      view.setUint16(offset, valueBytes.length, false);
      offset += 2;

      // Write header value
      new Uint8Array(headerBuffer, offset, valueBytes.length).set(valueBytes);

      encodedHeaders.push(headerBuffer);
      totalSize += headerSize;
    }

    // Combine all headers into a single buffer
    const combinedBuffer = new ArrayBuffer(totalSize);
    let offset = 0;

    for (const header of encodedHeaders) {
      new Uint8Array(combinedBuffer, offset, header.byteLength).set(
        new Uint8Array(header)
      );
      offset += header.byteLength;
    }

    return combinedBuffer;
  }

  /**
   * Calculate CRC32 checksum using the IEEE 802.3 standard (used by AWS)
   */
  private calculateCRC32(data: Uint8Array): number {
    const crcTable = this.getCRC32Table();
    let crc = 0xffffffff;

    for (let i = 0; i < data.length; i++) {
      const byte = data[i];
      crc = (crc >>> 8) ^ crcTable[(crc ^ byte) & 0xff];
    }

    return (crc ^ 0xffffffff) >>> 0; // Convert to unsigned 32-bit
  }

  /**
   * Get CRC32 lookup table (IEEE 802.3 standard)
   */
  private getCRC32Table(): Uint32Array {
    if (!this.crc32Table) {
      this.crc32Table = new Uint32Array(256);

      for (let i = 0; i < 256; i++) {
        let crc = i;
        for (let j = 0; j < 8; j++) {
          if (crc & 1) {
            crc = (crc >>> 1) ^ 0xedb88320;
          } else {
            crc = crc >>> 1;
          }
        }
        this.crc32Table[i] = crc >>> 0; // Convert to unsigned
      }
    }

    return this.crc32Table;
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  private async attemptReconnect(): Promise<void> {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error(
        "[HealthScribe WebSocket Provider] Max reconnection attempts reached"
      );
      if (this.onError) {
        this.onError("Max reconnection attempts reached");
      }
      return;
    }

    this.reconnectAttempts++;

    try {
      await new Promise((resolve) =>
        setTimeout(resolve, this.reconnectDelay * this.reconnectAttempts)
      );
      if (this.sessionId && this.clinicId && this.websocketUrl) {
        await this.connect(this.sessionId, this.clinicId, this.websocketUrl);
      }
    } catch (error) {
      console.error(
        "[HealthScribe WebSocket Provider] Reconnection failed:",
        error
      );
      await this.attemptReconnect();
    }
  }
}
